import {
    formatListings,
    formatListingsV2,
    buildCanonicalPath,
    saveSearchAndAlertUrlFixer
} from '@zoopla/listing-search-utils';
import {
    ServerData,
    ServerDataV2,
    SearchContextType,
    mapDataToActions,
    mapDataToActionsV2,
    searchReducer
} from '@zoopla/listing-search-context';
import { mapTargetingFilters } from '@zoopla/adverts/dfp';
import shuffle from 'lodash/shuffle';
import { internalPromoIds } from '@zoopla/listing-search/featured/properties';

type PromoIds = typeof internalPromoIds[number];

function getFeaturedListingsToDisplay<T>(
    properties: (T | PromoIds)[]
): (T | PromoIds)[] {
    const shuffledPromos = shuffle(internalPromoIds);
    const items = properties.concat(shuffledPromos);

    const maxItems = 3;
    const minProperties = 1;
    return properties.length >= minProperties ? items.slice(0, maxItems) : [];
}

export const getServerRenderedData = (
    serverSideData: ServerData,
    initialState: SearchContextType
) => {
    const {
        searchResults: {
            metaInfo,
            listings,
            pagination: {
                totalResults,
                pageNumber,
                totalResultsWasLimited,
                pageNumberMax
            },
            sortOrder,
            filters: { fields },
            analyticsTaxonomy,
            links,
            polyenc,
            polygon,
            seoBlurb: { transactionType }
        }
    } = serverSideData;
    const { regular, extended, featured } = listings;

    const searchContextData = mapDataToActions(serverSideData).reduce(
        searchReducer,
        initialState
    );

    const canonicalPath = buildCanonicalPath(metaInfo.canonicalUri || '');

    const formattedLinks = {
        createAlert: saveSearchAndAlertUrlFixer(links?.createAlert),
        saveSearch: saveSearchAndAlertUrlFixer(links?.saveSearch)
    };

    return {
        regularListingsFormatted: formatListings(regular),
        extendedListingsFormatted: formatListings(extended),
        featuredProperties: getFeaturedListingsToDisplay(featured ?? []),
        searchContextData,
        canonicalPath,
        totalResults,
        sortOrder,
        filtersFields: fields,
        pageNumber,
        pageNumberMax,
        totalResultsWasLimited,
        analyticsTaxonomy,
        polyenc,
        polygon,
        filtersTargeting: mapTargetingFilters(fields),
        transactionType,
        links: formattedLinks
    };
};

export const getServerRenderedDataV2 = (
    serverSideData: ServerDataV2,
    initialState: SearchContextType
) => {
    const {
        searchResultsV2: {
            metaInfo,
            results,
            pagination: {
                totalResults,
                pageNumber,
                totalResultsWasLimited,
                pageNumberMax
            },
            sortOrder,
            filters: { fields },
            analyticsTaxonomy,
            links,
            polyenc,
            polygon,
            seoBlurb: { transactionType }
        }
    } = serverSideData;
    const { main, extended } = results;

    const searchContextData = mapDataToActionsV2(serverSideData).reduce(
        searchReducer,
        initialState
    );

    const canonicalPath = buildCanonicalPath(metaInfo.canonicalUri || '');

    const formattedLinks = {
        createAlert: saveSearchAndAlertUrlFixer(links?.createAlert),
        saveSearch: saveSearchAndAlertUrlFixer(links?.saveSearch)
    };

    return {
        regularListingsFormatted: formatListingsV2(main),
        extendedListingsFormatted: formatListings(extended),
        featuredProperties: getFeaturedListingsToDisplay([]),
        searchContextData,
        canonicalPath,
        totalResults,
        sortOrder,
        filtersFields: fields,
        pageNumber,
        pageNumberMax,
        totalResultsWasLimited,
        analyticsTaxonomy,
        polyenc,
        polygon,
        filtersTargeting: mapTargetingFilters(fields),
        transactionType,
        links: formattedLinks
    };
};
